import { FieldType } from '@databases/model/field';
import {
  DatabaseEntry,
  DatabaseField,
  DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import { Cheerio } from 'cheerio';
import { Element } from 'domhandler';
import { ParseResult } from 'papaparse';

export type ParsedData = Record<string, unknown> & { __parsed_extra?: unknown[] };

export interface ExtendedParseResult<T> extends ParseResult<T> {
  data: Array<T & { __parsed_extra?: unknown[] }>;
}

// TODO: NCTD-2880 add FieldType.Select
export type CSVSupportedFieldTypes =
  | FieldType.Checkbox
  | FieldType.Date
  | FieldType.Hyperlink
  | FieldType.Number
  | FieldType.Text;

export type FieldTypeMapping = Map<string, CSVSupportedFieldTypes>;

export type RawFile = {
  name: string;
  mimeType: string;
  rawContent: string;
  size: number;
  type: ImportedFileType;
};

export type ParsedPasteFile = RawFile &
  (
    | {
        type: ImportedFileType.HTML;
        content: Array<Array<Cheerio<Element> | undefined>>;
      }
    | {
        type: ImportedFileType.CSV;
        content: string;
      }
  );

export type ParsedImportFile = Extract<
  ParsedPasteFile,
  {
    type: ImportedFileType.CSV;
    content: string;
  }
>;

export enum ImportOption {
  MAP = 'map',
  APPEND = 'append',
  REPLACE = 'replace',
  CREATE = 'create',
}

export enum ImportFieldMode {
  EXISTING = 'existing',
  CREATE = 'create',
  IGNORE = 'ignore',
}

export type ImportFieldReference =
  | { type: ImportFieldMode.EXISTING; fieldId: string }
  | { type: ImportFieldMode.CREATE; field: DatabaseField }
  | { type: ImportFieldMode.IGNORE };

export enum ImportEntryMode {
  EXISTING = 'existing',
  CREATE = 'create',
}

export type ImportEntryReference =
  | { type: ImportEntryMode.EXISTING; entryId: string }
  | { type: ImportEntryMode.CREATE; entry: DatabaseEntry };

export interface ImportSettings {
  /**
   * Which field to map each column to, in order.
   * A string references an existing database field by ID.
   * A DatabaseField object represents a database field that should be newly created.
   * null or undefined means that the field should be ignored.
   */
  fieldReferences: Array<ImportFieldReference | undefined>;
  /** For each row whether it should be imported or not. The default is true. */
  selectedRows: boolean[];

  /**
   * Which entry to map each row to, in order.
   * */
  entryReferences: Array<ImportEntryReference | undefined>;
  /**
   * For each column whether it should be imported or not. The default is true.
   * */
  selectedColumns: boolean[];
}

export interface ImportValues {
  /** A text representation of the cell content. */
  text: string;
  /** The cell parsed as a database value. Undefined if no field is selected for this column. */
  value?: DatabaseValue;
  /**
   * Is true if the parsed database value is empty. The cause may be that the imported cell is empty or that it does not contain
   * any data that is compatible with the selected field type.
   */
  isTargetEmpty: boolean;
  targetFieldId: string | undefined;
  targetEntryId: string | undefined;
  targetIsMulti: boolean | undefined;
  isSourceEmpty: boolean | undefined;
}

export interface ImportResult {
  /** The number of columns in the imported data. */
  colCount: number;
  /** The parsed data rows and cells in the imported data. */
  rows: Array<{
    /** Whether the row is selected to be imported in the import settings. */
    isSelected: boolean;

    cols: Array<ImportValues>;
  }>;
}

// Don't auto sort, step order matters
export enum ImportSteps {
  fileReceived,
  csvParsed,
  conversionCompleted,
  importCompleted,
}

export type ImportConfig = {
  option: ImportOption;
  headerRow?: boolean;
};
